<template>
  <GenericCard
    :locked="locked"
    has-delete
    :subtitle="subtitle"
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="isSectionValid"
    :dirty="isSectionDirty"
    :create="create"
    title="Phone"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <PhoneFields
        v-model="section"
        :is-dirty.sync="isSectionDirty"
        :is-valid.sync="isSectionValid"
      />
    </v-form>
    <template #header>
      <!-- Preferred Voice -->
      <v-chip
        v-if="section.preferred_voice"
        class="ml-2"
        :color="phoneVoiceable ? 'success' :'error'"
        outlined
        label
      >
        <v-icon
          small
          left
        >
          mdi-check
        </v-icon>
        Voice
      </v-chip>
      <v-btn
        v-if="!section.preferred_voice && phoneVoiceable"
        class="ml-2"
        small
        @click="voiceDialog = true"
      >
        Voice
      </v-btn>
      <!-- Preferred Text -->
      <v-chip
        v-if="section.preferred_text"
        class="ml-2"
        :color="phoneTextable ? 'success' :'error'"
        outlined
        label
      >
        <v-icon
          small
          left
        >
          mdi-check
        </v-icon>
        Text
      </v-chip>
      <v-btn
        v-if="!section.preferred_text && phoneTextable"
        class="ml-2"
        small
        @click="textDialog = true"
      >
        Text
      </v-btn>
    </template>
    <v-dialog
      v-model="voiceDialog"
      persistent
      max-width="290"
    >
      <v-card>
        <v-card-title class="text-h5">
          Set Preferred Voice
        </v-card-title>
        <v-card-text>Are you sure you want to set <b>{{ section.phone_number }}</b> as preferred voice?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="voiceDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="onSetPreferredVoice"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="textDialog"
      persistent
      max-width="290"
    >
      <v-card>
        <v-card-title class="text-h5">
          Set Preferred Text
        </v-card-title>
        <v-card-text>Are you sure you want to set <b>{{ section.phone_number }}</b> as preferred text?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="textDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="onSetPreferredText"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'
import PhoneFields from './PhoneFields.vue'

export default {
  name: 'PhoneCard',

  components: {
    GenericCard,
    PhoneFields,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      isSectionDirty: false,
      isSectionValid: true,
      section: {},
      textDialog: false,
      voiceDialog: false,
    }
  },

  computed: {
    cardIdentifier () { return `phone-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.pePhoneCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPePhoneCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    phoneTextable () {
      return this.phoneTypesOptions?.find(phone => phone.value === this.section.phone_type)?.textable
    },

    phoneTypesOptions () { return this.getCachedDropdown('phones/phone_type') },

    phoneVoiceable () {
      return this.phoneTypesOptions?.find(phone => phone.value === this.section.phone_type)?.voiceable
    },

    preferredVoiceBtnColor () { return this.section.preferred_voice ? 'success' : 'primary' },
    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      const subtitleText = this.phoneTypesOptions?.find(phone => phone.value === this.section.phone_type)?.text
      return `(${this.section.owner || ''} <${this.section.phone_number}> - ${subtitleText})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'pePhoneCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPePhoneCard(this.sectionIdentifier)
  },

  methods: {
    async onCardCreate () {
      return this.pePhoneCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.pePhoneCardCreateUndo()
      else {
        return this.pePhoneCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.pePhoneCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.pePhoneCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPePhoneCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async onSetPreferredText () {
      this.section.preferred_text = true
      this.textDialog = false
      return this.onCardUpdate()
    },

    async onSetPreferredVoice () {
      this.section.preferred_voice = true
      this.voiceDialog = false
      return this.onCardUpdate()
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'pePhoneCardCreateUndo',
      'setPePhoneCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPePhoneCard',
      'pePhoneCardCreate',
      'pePhoneCardDelete',
      'pePhoneCardRefresh',
      'pePhoneCardUpdate',
    ]),
  },
}
</script>
